import { useState, type ReactNode } from 'react';

import { Callout, Field, Panel, PasswordInput, TextInput } from './ui.tsx';
import { mfaConfirmBody } from './mfa-ticket.ts';
import { kitText } from './messages.ts';
import { PASSWORD_MIN_LENGTH, passwordRules } from './password-policy.ts';
import { TotpQrCode } from './totp-qr.tsx';

/**
 * The two steps between being given an account and being able to use one.
 *
 * §3.7 makes both mandatory and neither is a formality: a password an
 * administrator chose is one two people know, and an account with no second
 * factor is one the password alone opens. A console that let somebody past
 * either would have implemented the rule's shape and not the rule.
 */

export interface OnboardApi {
  /** The secret is minted here and returned to the person, never to an admin. */
  enrol(body: { email: string; password: string }): Promise<{ secret: string; uri: string; ticket: string; message: string }>;
  /** `ticket` is the one enrol returned: it names the enrolment being confirmed. */
  confirm(body: { email: string; code: string; ticket: string }): Promise<{ enrolled: boolean }>;
  changePassword(body: {
    email: string;
    current_password: string;
    new_password: string;
  }): Promise<{ changed?: boolean; message?: string }>;
}

export function EnrolMfa({
  email,
  password,
  api,
  onDone,
  onCancel,
  title = kitText('onboard.title'),
  intro,
}: {
  email: string;
  /**
   * Given already (the forced flow, where it is the password just used to
   * sign in) or omitted, for a voluntary set-up reached from an Account
   * screen long after signing in — the same split `ChangePassword` above
   * makes, for the same reason. The app never retains the password, and
   * `/v1/mfa/enrol` re-checks it itself, so the person types it again.
   */
  password?: string;
  api: OnboardApi;
  onDone: () => void;
  /** Offered only where there is somewhere to go back to; the forced flow has none. */
  onCancel?: () => void;
  title?: string;
  intro?: ReactNode;
}) {
  const askPassword = password === undefined;
  const [given, setGiven] = useState(password ?? '');
  const [started, setStarted] = useState<{ secret: string; uri: string; ticket: string; message: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const begin = () => {
    if (busy || given === '') return;
    setBusy(true);
    setProblem(null);
    api
      .enrol({ email, password: given })
      .then((r) => setStarted({ secret: r.secret, uri: r.uri, ticket: r.ticket, message: r.message }))
      .catch(() =>
        setProblem(
          askPassword
            ? kitText('onboard.badPassword')
            : kitText('onboard.startFailed'),
        ),
      )
      .finally(() => setBusy(false));
  };

  const finishSetup = () => {
    if (busy || code.length !== 6 || !started) return;
    setBusy(true);
    setProblem(null);
    api
      .confirm(mfaConfirmBody(email, code, started.ticket))
      .then(onDone)
      .catch(() => setProblem(kitText('onboard.codeMismatch')))
      .finally(() => setBusy(false));
  };

  return (
    <div className="main signin">
      <div className="page-head"><h1>{title}</h1></div>

      <Panel title={kitText('onboard.panel')}>
        <div className="form">
          {problem ? <Callout tone="error">{problem}</Callout> : null}

          {!started ? (
            <>
              {/* §3.7 — said before the button, because somebody who thinks
                  this is optional will close the tab and be locked out. */}
              <Callout>
                {intro ?? kitText('onboard.intro')}
              </Callout>

              {/* Asked again rather than remembered: enrolling a second factor
                  is a change to how this account is protected, and a session
                  left open on an unlocked machine should not be enough to make
                  one. `/v1/mfa/enrol` checks it server-side regardless. */}
              {askPassword ? (
                <Field label={kitText('field.yourPassword')}>
                  <PasswordInput
                    value={given}
                    onChange={setGiven}
                    autoComplete="current-password"
                  />
                </Field>
              ) : null}

              <div className="form-actions">
                {onCancel ? (
                  <button className="copy" disabled={busy} onClick={onCancel}>
                    {kitText('action.cancel')}
                  </button>
                ) : null}
                <button
                  className="primary"
                  disabled={busy || given === ''}
                  onClick={begin}
                >
                  {busy ? kitText('action.starting') : kitText('onboard.start')}
                </button>
              </div>
            </>
          ) : (
            <>
              <Callout>{started.message}</Callout>

              <div className="totp-setup">
                <TotpQrCode uri={started.uri} />
                <Field
                  label={kitText('onboard.key')}
                  hint={kitText('onboard.key.hint')}
                >
                  {/* Shown once. It is not stored anywhere this console can read
                      again, and nobody else — including whoever created the
                      account — has ever seen it. */}
                  <code className="preview">{started.secret}</code>
                </Field>
              </div>

              <Field label={kitText('onboard.code')} hint={kitText('field.code.hint')}>
                <TextInput
                  value={code}
                  onChange={setCode}
                  placeholder="000000"
                  onKeyDown={(e) => { if (e.key === 'Enter') finishSetup(); }}
                />
              </Field>

              <div className="form-actions">
                <button
                  className="primary"
                  disabled={busy || code.length !== 6}
                  onClick={finishSetup}
                >
                  {busy ? kitText('action.checking') : kitText('onboard.finish')}
                </button>
              </div>
            </>
          )}
        </div>
      </Panel>
    </div>
  );
}

function PasswordRules({ value, again }: { value: string; again: string }) {
  const rules = passwordRules(value, again);
  return (
    <ul className="password-rules">
      {rules.map((r) => (
        <li key={r.label} className={r.met ? 'met' : ''}>
          {/*
            The tick is decorative — the state is also in the row's text
            colour and, for a screen reader, in the label that follows.
            Colour alone would carry it for nobody using one.
          */}
          <span aria-hidden="true">{r.met ? '✓' : '·'}</span>
          <span>{r.label}</span>
          <span className="sr-only">
            {r.met ? kitText('password.rule.met') : kitText('password.rule.notMet')}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function ChangePassword({
  email,
  currentPassword,
  api,
  onDone,
  title = kitText('password.title'),
  intro,
}: {
  email: string;
  /**
   * Given already (the forced first-login flow, where it is the password
   * just used to sign in) or omitted — item 2 (third batch) — for a
   * voluntary change reached from an Account screen long after signing in,
   * where the app never retains it and the person has to prove they still
   * know it by typing it again.
   */
  currentPassword?: string;
  api: OnboardApi;
  onDone: () => void;
  title?: string;
  intro?: ReactNode;
}) {
  const askCurrent = currentPassword === undefined;
  const [current, setCurrent] = useState(currentPassword ?? '');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const mismatch = again !== '' && next !== again;

  return (
    <div className="main signin">
      <div className="page-head"><h1>{title}</h1></div>

      <Panel title={kitText('password.panel')}>
        <div className="form">
          {problem ? <Callout tone="error">{problem}</Callout> : null}

          {/* §3.7 — the reason, not just the requirement. Somebody told only
              "you must change your password" reuses the temporary one with a
              digit on the end. */}
          <Callout>
            {intro ?? kitText('password.intro')}
          </Callout>

          {askCurrent ? (
            <Field label={kitText('field.currentPassword')}>
              <PasswordInput value={current} onChange={setCurrent} autoComplete="current-password" />
            </Field>
          ) : null}

          <Field label={kitText('field.newPassword')}>
            <PasswordInput
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              minLength={PASSWORD_MIN_LENGTH}
            />
          </Field>

          {/*
            What the server will actually accept, ticking as it is typed —
            rather than one "12 characters minimum" rejection after the fact.
            §3.7's own requirement is length, and length alone: there is no
            uppercase/digit/symbol rule anywhere behind this form, so this
            list does not invent one. A checklist showing a red cross beside a
            rule the server would have accepted teaches people to distrust the
            whole form, and composition rules mostly buy "Password1!" anyway.
          */}
          <PasswordRules value={next} again={again} />

          <Field
            label={kitText('field.again')}
            {...(mismatch ? { error: kitText('password.mismatch') } : {})}
          >
            <PasswordInput value={again} onChange={setAgain} autoComplete="new-password" />
          </Field>

          <div className="form-actions">
            <button
              className="primary"
              disabled={busy || current === '' || next.length < PASSWORD_MIN_LENGTH || mismatch || again === ''}
              onClick={() => {
                setBusy(true);
                setProblem(null);
                api
                  .changePassword({ email, current_password: current, new_password: next })
                  .then((r) => (r.changed ? onDone() : setProblem(r.message ?? kitText('password.failed'))))
                  .catch(() => setProblem(kitText('password.unreachable')))
                  .finally(() => setBusy(false));
              }}
            >
              {busy ? kitText('action.saving') : kitText('password.submit')}
            </button>
          </div>
        </div>
      </Panel>
    </div>
  );
}
